import { StateStorage } from "client/zustand/notPublic";
import { IndexedDbStateStorage, IsolatedLocalStorageStateStorage } from "client/zustand/persistence";
import { RecentlyOpenedResultsStoreData } from "./useRecentlyOpenedResults";
import { decrypt, encrypt, SjclCipherEncrypted } from "sjcl";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getEncryptionKey } from "client/Api/FrontEndEncryption/frontEndEncryption";
import { GlobalSearchItemPersistedModel } from "./models";
import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { getNumericEnumValues } from "client/Misc/utils";
import { MigrationMetadata, WithMigratorStorageValue } from "client/zustand/migrations";

export async function serializeState(value: WithMigratorStorageValue<RecentlyOpenedResultsStoreData>): Promise<string> {
	const {
		state,
		version,
		migrationMetadata
	} = value;

	const {
		databaseConfiguration: {
			encryptionKey,
			encryptionPublic
		}
	} = await waitForUserConfigurationToInitialize();

	const {
		items
	} = state;

	const itemsContent: string = JSON.stringify(items);
	const encryptedContent: SjclCipherEncrypted = encrypt(encryptionKey, itemsContent);

	const model: PersistedModel = {
		content: encryptedContent,
		encryptionPublic,
		version,
		migrationMetadata
	};

	return JSON.stringify(model);
}

export async function deserializeState(serializedState: string): Promise<WithMigratorStorageValue<RecentlyOpenedResultsStoreData>> {
	const {
		databaseConfiguration: {
			encryptionKey: currentEncryptionKey,
			encryptionPublic: currentEncryptionPublic
		}
	} = await waitForUserConfigurationToInitialize();

	const model: PersistedModel = JSON.parse(serializedState);

	const {
		encryptionPublic,
		version,
		content,
		migrationMetadata
	} = model;

	const encryptionKey: string = encryptionPublic === currentEncryptionPublic
		? currentEncryptionKey
		: await getEncryptionKey(encryptionPublic);

	const decryptedContent: string = decrypt(encryptionKey, content);

	const parsedItems: readonly GlobalSearchItemPersistedModel[] = JSON.parse(decryptedContent);
	const items: readonly GlobalSearchItemPersistedModel[] = parsedItems.filter(isSupportedRecentlyOpenedItem);

	const state: Partial<RecentlyOpenedResultsStoreData> = {
		items
	};

	return {
		// NOTE: Type definitions for zustand require "deserialize" to return full state
		// But actually result of the deserialize method is passed to the zustand's set method,
		// which accepts partial state. If we pass entire state including placeholders for methods,
		// they will be overridden with these placeholders
		state: state as RecentlyOpenedResultsStoreData,
		version,
		migrationMetadata
	};
}

function isSupportedRecentlyOpenedItem(item: GlobalSearchItemPersistedModel): boolean {
	return getNumericEnumValues(GlobalSearchResultItemKind).includes(item.kind);
}

export class SafeStateStorage implements StateStorage {
	private readonly _storage: Promise<StateStorage>;

	constructor() {
		const fallbackStorage = new IsolatedLocalStorageStateStorage();

		const primaryStorage = new IndexedDbStateStorage({
			onAfterSetItem: (name) => fallbackStorage.clear(name),
			shouldOnlyPersistDataForCurrentUser: false
		});

		this._storage = primaryStorage.checkIfAvailable()
			.then(isAvailable => isAvailable ? primaryStorage : fallbackStorage);
	}

	public async getItem(name: string): Promise<string | null> {
		const storage: StateStorage = await this._storage;

		return await storage.getItem(name);
	}

	public async setItem(name: string, value: string): Promise<void> {
		const storage: StateStorage = await this._storage;

		await storage.setItem(name, value);
	}
}

type PersistedModel = {
	content: SjclCipherEncrypted;
	encryptionPublic: string;
	version: number;
	migrationMetadata: Record<number, MigrationMetadata>;
};